import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { FaArrowLeft, FaPaperPlane, FaDatabase, FaTable } from "react-icons/fa";

function safeJsonParse(value) {
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function firstValidValue(candidates) {
  return candidates.find((candidate) => {
    if (candidate === undefined || candidate === null) return false;
    const value = String(candidate).trim();
    return value && !["none", "null", "undefined"].includes(value.toLowerCase());
  });
}

function buildExcelEmbedUrl(url, key) {
  if (!url) return null;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}action=embedview&AllowTyping=True&wdDownloadButton=True&wdInConfigurator=True&t=${key || Date.now()}`;
}

const SpreadsheetAI = ({ onClose }) => {
  const [chatMessages, setChatMessages]     = useState([]);
  const [prompt, setPrompt]                 = useState("");
  const [isTyping, setIsTyping]             = useState(false);
  const [operationType, setOperationType]   = useState("database"); // "database" | "spreadsheet"
  const [iframeKey, setIframeKey]           = useState(Date.now());

  const userId = useSelector(
    (state) => state?.account?.user_id || state?.account?.org_member?.id
  );
  const orgId = useSelector(
    (state) => state?.account?.org?.id || state?.account?.org_data?.data?.id
  );
  const account = useSelector((state) => state?.account || {});
  const storedUser = safeJsonParse(localStorage.getItem("user"));
  const storedUserData = safeJsonParse(localStorage.getItem("user_data"));

  const fileId = firstValidValue([
    localStorage.getItem("doc_agent_excel_name"),
    localStorage.getItem("docAgentExcelFileId"),
    storedUser?.doc_agent_excel_name,
    storedUserData?.data?.doc_agent_excel_name,
    storedUserData?.UserDetails?.doc_agent_excel_name,
    account?.user?.doc_agent_excel_name,
    account?.UserDetails?.doc_agent_excel_name,
  ]);
  const spreadsheetExcelUrl = firstValidValue([
    localStorage.getItem("doc_agent_excel_url"),
    localStorage.getItem("docAgentExcelUrl"),
    storedUser?.doc_agent_excel_url,
    storedUserData?.data?.doc_agent_excel_url,
    storedUserData?.UserDetails?.doc_agent_excel_url,
    account?.user?.doc_agent_excel_url,
    account?.UserDetails?.doc_agent_excel_url,
  ]);
  const baseSharepointUrl = buildExcelEmbedUrl(spreadsheetExcelUrl, iframeKey);

  const handleOperationChange = (event, newType) => {
    if (newType !== null) {
      setOperationType(newType);
    }
  };

  const handleSendMessage = async () => {
    if (!prompt.trim()) return;
    const currentPrompt = prompt;
    const currentOpType = operationType;

    setChatMessages((prev) => [
      ...prev,
      {
        text:   currentPrompt,
        sender: "user",
        badge:  currentOpType === "spreadsheet" ? "📊 Sheet" : "🗄️ Data"
      },
    ]);
    setPrompt("");
    setIsTyping(true);

    const loadingSteps =
      currentOpType === "spreadsheet"
        ? [
            "📊 Analyzing Excel data...",
            "✏️ Preparing spreadsheet operation...",
            "⚡ Executing script...",
          ]
        : [
            "🔍 Retrieving data from database...",
            "📊 Analyzing data...",
            "💡 Generating insights...",
          ];

    let stepIndex = 0;
    const intervalRef = { current: null };

    setChatMessages((prev) => [
      ...prev,
      { text: loadingSteps[0], sender: "bot", loading: true },
    ]);

    intervalRef.current = setInterval(() => {
      stepIndex++;
      if (stepIndex < loadingSteps.length) {
        setChatMessages((prev) => {
          const filtered = prev.filter((msg) => !msg.loading);
          return [
            ...filtered,
            { text: loadingSteps[stepIndex], sender: "bot", loading: true },
          ];
        });
      }
    }, 1500);

    try {
      const token    = localStorage.getItem("token");
      const response = await fetch(
        "https://api.projectivetech.com/user/get_chat_xlsx/",
        //"http://localhost:8000/user/get_chat_xlsx/",
        //"http://localhost:8000/api/get-chat-xlsx-ai/",
        {
          method:  "POST",
          headers: {
            "Content-Type":  "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            prompt:         currentPrompt,
            orgId,
            userId,
            fileId,
            operation_type: currentOpType,   // ← sent to backend
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();
      clearInterval(intervalRef.current);

      setChatMessages((prev) => {
        const filtered = prev.filter((msg) => !msg.loading);
        return [...filtered, { text: result.response, sender: "bot" }];
      });

      if (result.updated_excel) {
        let updateValue = result.updated_excel;
        if (typeof updateValue === "object") {
          updateValue = updateValue.updated_excel || JSON.stringify(updateValue);
        }

        setChatMessages((prev) => [
          ...prev,
          { text: `📂 Excel Update: ${updateValue}`, sender: "system" },
        ]);

        // Wait for Office Script to finish then refresh iframe
        setChatMessages((prev) => [
          ...prev,
          { text: "⏳ Refreshing Excel...", sender: "system", loading: true },
        ]);

        const refreshDelay = currentOpType === "spreadsheet" ? 8000 : 8000;

        setTimeout(() => {
          setChatMessages((prev) => {
            const filtered = prev.filter((msg) => msg.text !== "⏳ Refreshing Excel...");
            return [...filtered, { text: "✅ Excel refreshed!", sender: "system" }];
          });
          setIframeKey(Date.now());
        }, refreshDelay);
      }

    } catch (error) {
      clearInterval(intervalRef.current);
      console.error("❌ AI request failed:", error);
      setChatMessages((prev) => {
        const filtered = prev.filter((msg) => !msg.loading);
        return [
          ...filtered,
          { text: "❌ Error: Could not process request.", sender: "bot" },
        ];
      });
    } finally {
      setIsTyping(false);
    }
  };

  const clearExcelData = async () => {
    try {
      const token    = localStorage.getItem("token");
      const response = await fetch(
        "https://api.projectivetech.com/user/clear_excel_data/",
        {
          method:  "POST",
          headers: {
            "Content-Type":  "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ file_id: fileId })
        }
      );
      const result = await response.json();
      console.log("Excel data cleared:", result.message);
    } catch (error) {
      console.error("❌ Failed to clear Excel:", error);
    }
  };

  const handleCloseChat = () => {
    clearExcelData().catch((error) =>
      console.error("❌ Failed to clear Excel:", error)
    );
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getBubbleColor = (msg) => {
    if (msg.sender === "user")   return "linear-gradient(45deg, #6a11cb, #2575fc)";
    if (msg.sender === "system") return "#2a5c3f";
    return "#545f6a";
  };

  return (
    <div
      style={{
        width:     "100%",
        height:    "100vh",
        background: "#12131A",
        color:     "white",
        display:   "flex",
        flexDirection: "column",
        overflow:  "hidden",
      }}
    >
      {/* MAIN CONTENT */}
      <Box display="flex" flex={1} p={2} sx={{ overflow: "hidden" }}>
        <Grid container spacing={2} sx={{ height: "100%" }}>

          {/* Excel Embed */}
          <Grid item xs={12} md={9} sx={{ height: "100%" }}>
            <Paper
              elevation={3}
              sx={{ width: "100%", height: "100%", borderRadius: "12px", overflow: "hidden" }}
            >
              {baseSharepointUrl ? (
                <iframe
                  id="excel-iframe"
                  key={iframeKey}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  src={baseSharepointUrl}
                  title="Embedded Excel"
                />
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                    p: 4,
                    textAlign: "center",
                    bgcolor: "#f8fafc",
                    color: "#334155",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>No workbook connected</Typography>
                    <Typography sx={{ mt: 1, fontSize: 14, color: "#64748b" }}>
                      Assign a Document Agent Excel URL to this user to show the live workbook.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Chat Panel */}
          <Grid item xs={12} md={3} sx={{ height: "100%" }}>
            <Paper
              elevation={3}
              sx={{
                display:        "flex",
                flexDirection:  "column",
                width:          "100%",
                height:         "100%",
                borderRadius:   "16px",
                background:     "rgb(244, 244, 244)",
                backdropFilter: "blur(10px)",
                overflow:       "hidden",
              }}
            >
              {/* Operation Type Toggle */}
              <Box
                sx={{
                  px: 2,
                  pt: 2,
                  pb: 1,
                  background: "rgba(0,0,0,0.05)",
                  borderBottom: "1px solid rgba(0,0,0,0.1)"
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#555", fontWeight: 600, display: "block", mb: 0.5 }}
                >
                  Query Type
                </Typography>
                <ToggleButtonGroup
                  value={operationType}
                  exclusive
                  onChange={handleOperationChange}
                  size="small"
                  fullWidth
                >
                  <Tooltip title="Fetch data from Power BI database">
                    <ToggleButton
                      value="database"
                      sx={{
                        fontSize:  "0.75rem",
                        fontWeight: 600,
                        "&.Mui-selected": {
                          background: "linear-gradient(45deg, #6a11cb, #2575fc)",
                          color:      "white",
                        }
                      }}
                    >
                      <FaDatabase style={{ marginRight: 4 }} />
                      Database
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="Operate on existing Excel data">
                    <ToggleButton
                      value="spreadsheet"
                      sx={{
                        fontSize:  "0.75rem",
                        fontWeight: 600,
                        "&.Mui-selected": {
                          background: "linear-gradient(45deg, #11cb6a, #25fc75)",
                          color:      "white",
                        }
                      }}
                    >
                      <FaTable style={{ marginRight: 4 }} />
                      Spreadsheet
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Box>

              {/* Messages */}
              <Box
                sx={{
                  flex:          1,
                  padding:       "16px",
                  display:       "flex",
                  flexDirection: "column",
                  gap:           "12px",
                  overflowY:     "auto",
                }}
              >
                {chatMessages.length === 0 && (
                  <Typography
                    sx={{
                      color:     "#999",
                      fontSize:  "0.85rem",
                      textAlign: "center",
                      mt:        4
                    }}
                  >
                    {operationType === "database"
                      ? "Ask about your financial data.\nE.g. 'What is my monthwise revenue?'"
                      : "Operate on Excel data.\nE.g. 'Sort by revenue descending'"}
                  </Typography>
                )}

                {chatMessages.map((msg, index) => (
                  <Box key={index} sx={{ display: "flex", flexDirection: "column",
                    alignItems: msg.sender === "user" ? "flex-end" : "flex-start" }}>
                    {msg.badge && (
                      <Typography sx={{ fontSize: "0.65rem", color: "#999", mb: 0.3 }}>
                        {msg.badge}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        padding:      "10px 14px",
                        borderRadius: "10px",
                        fontSize:     "0.9rem",
                        maxWidth:     "90%",
                        color:        "#fff",
                        background:   getBubbleColor(msg),
                        boxShadow:    "0 2px 5px rgba(0,0,0,0.2)",
                        whiteSpace:   "pre-wrap",
                      }}
                    >
                      {msg.text}
                      {msg.loading && (
                        <span className="spinner" style={{ marginLeft: "8px", display: "inline-block" }} />
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Input */}
              <Box
                sx={{
                  display:    "flex",
                  alignItems: "center",
                  gap:        1,
                  px:         2,
                  py:         2,
                  background: "rgba(0, 0, 0, 0.59)",
                }}
              >
                <TextField
                  fullWidth
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={
                    operationType === "spreadsheet"
                      ? "Sort, filter, format, calculate..."
                      : "Ask about your data..."
                  }
                  variant="outlined"
                  multiline
                  maxRows={3}
                  sx={{
                    borderRadius: "12px",
                    background:   "transparent",
                    color:        "white",
                    "& .MuiOutlinedInput-root": {
                      color:        "white",
                      borderRadius: "12px",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                    },
                    "& input, & textarea": { color: "white" },
                  }}
                />
                <Button
                  onClick={handleSendMessage}
                  variant="contained"
                  disabled={isTyping || !prompt.trim()}
                  sx={{
                    minWidth:  "44px",
                    height:    "44px",
                    borderRadius: "12px",
                    background:
                      operationType === "spreadsheet"
                        ? "linear-gradient(45deg, #11cb6a, #25fc75)"
                        : "linear-gradient(45deg, #6a11cb, #2575fc)",
                    "&:disabled": { opacity: 0.5 }
                  }}
                >
                  <FaPaperPlane />
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <style>
        {`
          @keyframes spin {
            0%   { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .spinner {
            width:        16px;
            height:       16px;
            border:       2px solid rgba(255,255,255,0.3);
            border-top:   2px solid #fff;
            border-radius: 50%;
            animation:    spin 0.8s linear infinite;
            vertical-align: middle;
          }
        `}
      </style>
    </div>
  );
};

export default SpreadsheetAI;
